/**
 * Overlays real (data-driven) knockout ties onto a complete skeleton bracket, PER SLOT, so the tree
 * always renders as a full, clean halving shape (Round of 32 → Final) while showing real teams the
 * moment each individual tie is known — not waiting for a whole round to be drawn.
 *
 * Why per-slot: the bracket geometry is count-driven and assumes every round holds exactly double
 * the next, so each rendered round must stay full-size. Real ties are pre-placed into fixed-size
 * slot arrays (see `computeKnockoutSlotsByKey`); here we drop each real tie into its slot and keep
 * the skeleton's "Winner of …" placeholder for every still-empty slot. So the first Round-of-16
 * games appear as soon as their Round-of-32 feeders finish, alongside placeholders for the rest.
 */

import type { BracketMatch, BracketRound, KnockoutBracketModel } from '../types/knockoutBracket.types'

export function mergeKnockoutBracket(
  skeleton: KnockoutBracketModel,
  realSlotsByKey: Map<string, (BracketMatch | null)[]>,
  realThirdPlace: BracketMatch | null,
): KnockoutBracketModel {
  const rounds: BracketRound[] = skeleton.rounds.map((skel) => {
    const slots = realSlotsByKey.get(skel.key)
    // Only overlay when the slot array lines up with the skeleton round; otherwise keep the
    // full placeholder round so the halving geometry never sees a mismatched size.
    if (slots == null || slots.length !== skel.matches.length) return skel
    return {
      ...skel,
      matches: skel.matches.map((placeholder, i) => slots[i] ?? placeholder),
    }
  })

  return {
    rounds,
    thirdPlace: realThirdPlace ?? skeleton.thirdPlace ?? null,
  }
}
